import type { ScreenerTickerData, ScreenerTimeframe } from '@/types'

export type ChartTimeRange = { from: number; to: number }
// Two visible hours plus the pan buffer stay below the API's 5,000 buckets.
export const RAW_HISTORY_VIEW_SECONDS = 2 * 3600
export const SCREENER_INTERVAL_SECONDS: Record<string, number> = {
  '5s': 5, '1m': 60, '5m': 300, '30m': 1800, '1h': 3600, '4h': 14400, '1d': 86400,
}

export function boundHistoryRange(range: ChartTimeRange, interval: string): ChartTimeRange {
  return interval === '5s' && range.to - range.from > RAW_HISTORY_VIEW_SECONDS
    ? { from: range.to - RAW_HISTORY_VIEW_SECONDS, to: range.to } : range
}

export function rawHistoryDetailLabel(timeframe: ScreenerTimeframe | undefined,
  range: ChartTimeRange, browsing: boolean): string {
  const span = (seconds: number) => seconds % 86400 === 0 ? `${seconds / 86400}d`
    : seconds % 3600 === 0 ? `${seconds / 3600}h` : `${Math.round(seconds / 60)}m`
  const selected = timeframe?.mode === 'relative' ? timeframe.relative_seconds
    : timeframe?.mode === 'absolute' ? (timeframe.to_ts || 0) - (timeframe.from_ts || 0) : 0
  if (selected && selected > RAW_HISTORY_VIEW_SECONDS) {
    const selection = timeframe?.mode === 'relative' ? span(selected) : 'date range'
    return `5s detail · ${browsing ? '' : 'last '}${span(Math.round(range.to - range.from))} of selected ${selection}`
  }
  return '5s detail · up to 2h at a time'
}

export function initialHistoryRange(data: ScreenerTickerData | null, interval: string,
  timeframe?: ScreenerTimeframe, now = Date.now() / 1000): ChartTimeRange {
  if (timeframe?.mode === 'absolute' && timeframe.from_ts != null && timeframe.to_ts != null) {
    return boundHistoryRange({ from: timeframe.from_ts, to: timeframe.to_ts }, interval)
  }
  const step = SCREENER_INTERVAL_SECONDS[interval] || 60
  if (timeframe?.mode === 'relative' && timeframe.relative_seconds) {
    return boundHistoryRange({ from: now - timeframe.relative_seconds, to: now }, interval)
  }
  const timestamps = Object.values(data?.exchanges || {}).flatMap(exchange =>
    (exchange.oi_history || []).map(point => point.timestamp)).filter(Number.isFinite)
  const to = data?.oi_history_until || (timestamps.length ? Math.max(...timestamps) : now)
  const from = timestamps.length ? Math.min(...timestamps) : to - step * 200
  return { from: Math.min(to - step * 2, Math.max(from, to - step * 200)), to }
}

/** Virtual time space makes a fully fitted chart draggable beyond its loaded samples. */
export function historyDomain(range: ChartTimeRange, earliest?: number | null,
  now = Date.now() / 1000): ChartTimeRange {
  const span = Math.max(1, range.to - range.from)
  return {
    from: Math.min(range.from, Math.max(earliest ?? now - 365 * 86400, range.from - span)),
    // The display domain is independent of recorded coverage. Empty future
    // space is useful when following the newest bucket with a right offset.
    to: Math.max(now, range.to) + span,
  }
}

export function bufferedHistoryRange(range: ChartTimeRange, now = Date.now() / 1000): ChartTimeRange {
  // A week must not trigger another five days of speculative history reads.
  const padding = Math.min(15 * 60, Math.max(1, range.to - range.from) * 0.15)
  return { from: Math.floor(Math.max(0, Math.min(now, range.from - padding))), to: Math.floor(Math.min(now, range.to + padding)) }
}

export function historyWindowCovers(loaded: ChartTimeRange, visible: ChartTimeRange): boolean {
  return loaded.from <= visible.from && loaded.to >= Math.min(Date.now() / 1000, visible.to)
}
